<template>
  <CardLayout :title="`Tenant Ledger Report`">
    <template #header-actions>
      <DateTimeFilter
        v-model="dateRange"
        :time-zone="facilityTimeZone"
        :ranges="['Today', '2d', '1w', '1m', '2m', '3m']"
        default="1m"
        max-range="3m"
        custom
        inverted
      />
    </template>

    <v-data-table
      :headers="headers"
      :items="tenantLedgerReport"
      :loading="loading"
    >
      <template #item.action="{ item }">
        <template v-if="item.statusOfReport === 'Created'">
          <div class="btn-container">
            <ButtonDownload
              small
              @click="
                downloadReport(
                  item.dateOfReport,
                  item.organizationId,
                  item.pmsName
                )
              "
            >
              {{ item.pmsName.toLowerCase() === "storedge" ? "XLSX" : "CSV" }}
            </ButtonDownload>
            <ButtonSend
              small
              @click="
                openPopup(item.dateOfReport, item.organizationId, item.pmsName)
              "
            >
              Send Email
            </ButtonSend>
            <DialogLayout
              v-if="isPopupVisible"
              v-model="isPopupVisible"
              title="Send Email"
            >
              <div>
                <p v-if="formattedReportDate">
                  Report Date:
                  <strong>{{ formattedReportDate }}</strong>
                </p>
                <ValidatedTextField
                  v-model="emailId"
                  type="email"
                  name="Enter Your Email Address"
                  rules="required|min:10|max:250"
                />
              </div>
              <template #actions>
                <v-btn
                  color="primary"
                  text
                  @click="sendEmail"
                  :disabled="!isEmailValid"
                  >Send</v-btn
                >
                <v-btn color="primary" text @click="cancel">Cancel</v-btn>
              </template>
            </DialogLayout>
          </div>
        </template>
      </template>
      <template #item.initialEmailSentStatus="{ item }">
        {{ item.initialEmailSentStatus ? "Yes" : "No" }}
      </template>
      <template #item.dateOfReport="{ item }">
        {{ formatDate(item.dateOfReport, "UTC") }}
      </template>
    </v-data-table>
  </CardLayout>
</template>

<script>
import {
  SEND_TENANT_LEDGER_EMAIL,
  TENANT_LEDGER_REPORT_STATUS,
} from "./graphql";
import {
  TENANT_LEDGER_PMS_REPORT,
  STOREDGE_SUBSCRIPTIONS_BY_USER,
} from "./graphql";
import { computed } from "vue";
import CardLayout from "@layout/CardLayout.vue";
import useFacility from "../authentication/useFacility";
import useDataTable from "@components/common/useDataTable";
import DateTimeFilter from "@tod-ui/components/DateTimeFilter.vue";
import ButtonDownload from "@atoms/ButtonDownload.vue";
import ButtonSend from "@atoms/ButtonSend.vue";
import { formatDate, getCustomDate } from "@tod-ui/helpers/datetime";
import useUser from "../authentication/useUser";
import DialogLayout from "@layout/DialogLayout.vue";
import ValidatedTextField from "@components/utils/ValidatedTextField.vue";
import useAlerts from "@tod-ui/composables/useAlerts";
import * as XLSX from "xlsx";

export default {
  name: "TenantLedgerReport",
  components: {
    DateTimeFilter,
    CardLayout,
    ButtonDownload,
    ButtonSend,
    DialogLayout,
    ValidatedTextField,
  },
  setup() {
    const { addAlert, clearAllAlerts } = useAlerts();
    const { facilityTimeZone, facilityFormatDateTime4Humans } = useFacility();
    const { tableState, showAllItems, restoreItemsPerPage } = useDataTable({
      itemsPerPage: 10,
      sortBy: ["id desc"],
    });
    const { defaultOrganization } = useUser();
    // Must be a computed: `defaultOrganization` resolves asynchronously, and a
    // a plain ref captured it once at setup — leaving it undefined forever when
    // the user landed later, so `shouldSkip` stayed true and it never loaded.
    const currentOrganizationId = computed(() => defaultOrganization.value?.id);

    return {
      tableState,
      showAllItems,
      restoreItemsPerPage,
      defaultOrganization,
      facilityTimeZone,
      reportDateTime: facilityFormatDateTime4Humans,
      formatDate,
      currentOrganizationId,
      addAlert,
    };
  },
  data() {
    return {
      emailId: "",
      dateRange: {
        after: undefined,
        before: undefined,
      },
      tenantLedgerReport: [],
      filter: {},
      isDownloading: false,
      isPopupVisible: false,
      currentReportDate: "",
      selectedOrganizationId: null,
      pmsName: "",
    };
  },
  computed: {
    loading() {
      return this.$apollo.queries.tenantLedgerReport.loading;
    },
    shouldSkip() {
      return !this.dateRange?.after || !this.currentOrganizationId;
    },
    headers() {
      return [
        { text: "Name of Report", value: "nameOfReport", width: 200 },
        { text: "Date", value: "dateOfReport", width: 200 },
        { text: "Status", value: "statusOfReport", width: 200 },
        { text: "Action", value: "action", width: 200 },
      ];
    },
    formattedReportDate() {
      return this.formatDate(this.currentReportDate, "UTC");
    },
    isEmailValid() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(this.emailId);
    },
  },
  apollo: {
    tenantLedgerReport: {
      query: TENANT_LEDGER_REPORT_STATUS,
      variables() {
        const variables = {
          organizationId: this.currentOrganizationId,
          dateTimeFilter: {
            after: this.dateRange.after,
            before: this.dateRange.before || getCustomDate({ days: 1 }),
          },
          ...this.filter,
        };
        return variables;
      },
      skip() {
        return this.shouldSkip;
      },
      update(data) {
        const tenantLedgerLogs = data?.tenantLedgerLogs;
        if (!tenantLedgerLogs) {
          return [];
        }
        return tenantLedgerLogs.map((report) => {
          return {
            ...report,
            dateOfReport: report.dateOfReport,
          };
        });
      },
      error(error) {},
    },
  },
  methods: {
    setDateRange(after, before) {
      this.dateRange = { after, before };
    },
    async downloadReport(dateOfReport, organizationId, pmsName) {
      const dateObject = new Date(dateOfReport);
      const year = dateObject.getUTCFullYear();
      const month = String(dateObject.getUTCMonth() + 1).padStart(2, "0");
      const day = String(dateObject.getUTCDate()).padStart(2, "0");
      const startOfTheDay = new Date(`${year}-${month}-${day}`).toISOString();
      const formattedDate = `${year}-${month}-${day}`;

      this.isDownloading = true;
      try {
        const response = await this.$apollo.query({
          query: TENANT_LEDGER_PMS_REPORT,
          variables: {
            organizationId,
            dateTimeFilter: {
              after: startOfTheDay,
              before: startOfTheDay,
            },
            pmsName,
          },
        });

        const reportData = response.data.tenantLedgerPmsReport;

        if (pmsName?.toLowerCase() === "sitelink") {
          const csvData = reportData.map((item) => {
            return {
              "Facility Name": item.pmsFacilityName,
              "Sitelink Site ID": item.siteId,
              "Sitelink Site Code": item.corpCode,
              "SD Start Date": item.sdStartDate,
              "Sitelink Unit ID": item.sdUnitId,
              "Sitelink Unit Name": item.sdUnitName,
              "Tenant First Name": item.tenantFirstName,
              "Tenant Last Name": item.tenantLastName,
              "SD End Date": item.sdEndDate,
              "Sitelink Charge ID (Name)": item.sdChargeId,
              "Sitelink Charge Amount": item.sdChargeAmount,
              "Actual Charge Amount": item.sdActualChargeAmount,
              "Ledger Quantity": item.sdLedgerQuantity,
              "SD Charge Reconciliation": item.sdChargeReconcilation
                ? "Yes"
                : "No",
              "Ledger ID": item.ledgerId,
              "Device Count": item.deviceCount,
            };
          });

          const csvContent = this.convertToCSV(csvData);
          const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;",
          });
          const link = document.createElement("a");
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute(
            "download",
            `TenantLedgerReport_${dateOfReport}.csv`
          );
          link.style.visibility = "hidden";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else if (pmsName?.toLowerCase() === "storedge") {
          const excelData = reportData.map((item) => {
            return {
              "Facility Name": item.pmsFacilityName,
              "StorEdge Company ID": item.corpCode,
              "StorEdge Facility ID": item.facilityId,
              "SD Effective Date": item.sdStartDate,
              "SD End Date": item.sdEndDate,
              "StorEdge Unit ID ": item.sdUnitId,
              "Unit Number": item.sdUnitName,
              "StorEdge Charge ID (Name)": item.sdChargeId,
              "StorEdge Charge Amount": item.sdChargeAmount,
              "SD Charge Reconciliation": item.sdChargeReconcilation
                ? "Yes"
                : "No",
              "Ledger ID": item.ledgerId,
              "Moved In By (Username)": item.pmsMoveInBy,
              //"Moved Out By(Username)": item.pmsMoveOutBy,
              "Charge added By (Username)": item.pmsChargeAddedBy,
              //"Charge Removed By(Username)": item.pmsChargeRemovedBy,
              "Transfer By (Username)": item.transferBy,
              "Transfer From": item.transferFrom,
              "Transferred Unit Type": item.transferredUnitType,
            };
          });

          const workbook = XLSX.utils.book_new();
          const worksheet = XLSX.utils.json_to_sheet(excelData);

          const headerRange = XLSX.utils.decode_range(worksheet["!ref"]);
          worksheet["!cols"] = Array(headerRange.e.c + 1).fill({ wch: 20 });

          const headerStyle = {
            font: { bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "4F81BD" } },
            alignment: { horizontal: "center" },
          };

          for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
            const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
            if (!worksheet[cellRef].s) worksheet[cellRef].s = {};
            worksheet[cellRef].s = headerStyle;
          }

          for (let row = 1; row <= headerRange.e.r; row++) {
            const rowStyle = {
              fill: {
                fgColor: { rgb: row % 2 ? "E9EDF4" : "FFFFFF" },
              },
              border: {
                top: { style: "thin", color: { rgb: "D3D3D3" } },
                bottom: { style: "thin", color: { rgb: "D3D3D3" } },
              },
            };

            for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
              const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
              if (!worksheet[cellRef].s) worksheet[cellRef].s = {};
              worksheet[cellRef].s = rowStyle;

              if (col === 9) {
                if (worksheet[cellRef].v) {
                  const value = parseFloat(worksheet[cellRef].v);
                  if (!isNaN(value)) {
                    worksheet[cellRef].z = "$#,##0.00";
                  }
                }
              }
            }
          }

          XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            `Tenant Ledger - ${formattedDate}`
          );

          try {
            const subscriptionsResponse = await this.$apollo.query({
              query: STOREDGE_SUBSCRIPTIONS_BY_USER,
              variables: {
                organizationId,
                fromDate: startOfTheDay.split("T")[0],
                toDate: startOfTheDay.split("T")[0],
                invokeFrom: "",
              },
            });

            if (
              subscriptionsResponse.data?.storedgeSubscriptionsByUser?.length >
              0
            ) {
              const subscriptionData =
                subscriptionsResponse.data.storedgeSubscriptionsByUser
                  .filter((item) => item.month !== null) // Filter out null rows if any
                  .map((item) => ({
                    Month: item.month || "",
                    "Company Name": item.companyName || "",
                    "User Name": item.userName || "",
                    "First Name": item.firstName || "",
                    "Last Name": item.lastName || "",
                    "Smart Unit Subscriptions Added":
                      item.smartUnitSubscriptionsAddInMonth || 0,
                    "Transfers to Smart Unit": item.transferredToSmartUnit || 0,
                    "Total Subscriptions by User":
                      item.totalSubscriptionsByUser || 0,
                  }));

              // Create worksheet from the data
              const subscriptionWorksheet =
                XLSX.utils.json_to_sheet(subscriptionData);

              // Set column widths
              subscriptionWorksheet["!cols"] = Array(6).fill({ wch: 20 });

              // Style the header row
              const subHeaderRange = XLSX.utils.decode_range(
                subscriptionWorksheet["!ref"]
              );
              for (
                let col = subHeaderRange.s.c;
                col <= subHeaderRange.e.c;
                col++
              ) {
                const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
                if (!subscriptionWorksheet[cellRef])
                  subscriptionWorksheet[cellRef] = { v: "", t: "s" };
                if (!subscriptionWorksheet[cellRef].s)
                  subscriptionWorksheet[cellRef].s = {};
                subscriptionWorksheet[cellRef].s = headerStyle;
              }

              // Style the data rows and ensure values are set correctly
              for (let row = 1; row <= subHeaderRange.e.r; row++) {
                const rowStyle = {
                  fill: { fgColor: { rgb: row % 2 ? "E9EDF4" : "FFFFFF" } },
                  border: {
                    top: { style: "thin", color: { rgb: "D3D3D3" } },
                    bottom: { style: "thin", color: { rgb: "D3D3D3" } },
                  },
                };

                for (
                  let col = subHeaderRange.s.c;
                  col <= subHeaderRange.e.c;
                  col++
                ) {
                  const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
                  if (!subscriptionWorksheet[cellRef]) {
                    // Create the cell if it doesn't exist
                    const data = subscriptionData[row - 1];
                    const keys = Object.keys(data);
                    const value = data[keys[col]] || "";
                    subscriptionWorksheet[cellRef] = {
                      v: value,
                      t: typeof value === "number" ? "n" : "s",
                    };
                  }

                  if (!subscriptionWorksheet[cellRef].s)
                    subscriptionWorksheet[cellRef].s = {};
                  subscriptionWorksheet[cellRef].s = rowStyle;

                  // Special formatting for the subscription count column (column 5)
                  if (
                    col === 5 &&
                    typeof subscriptionWorksheet[cellRef].v === "number"
                  ) {
                    subscriptionWorksheet[cellRef].z = "0";
                  }
                }
              }

              XLSX.utils.book_append_sheet(
                workbook,
                subscriptionWorksheet,
                "Subscriptions By User"
              );
            }
          } catch (error) {
            console.error("Error fetching subscriptions by user:", error);
          }

          const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
            cellStyles: true,
          });

          const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = `TenantLedger_${pmsName}_${formattedDate}.xlsx`;
          link.click();
          URL.revokeObjectURL(link.href);
        }
      } catch (error) {
        console.error("Error downloading report:", error);
        this.addAlert({
          type: "error",
          message: "Failed to download report",
          timeout: 5,
        });
      } finally {
        this.isDownloading = false;
      }
    },
    convertToCSV(data) {
      if (!data || data.length === 0) {
        alert("No data available.");
      }
      const headers = Object.keys(data[0]).join(",");
      const rows = data.map((row) => Object.values(row).join(",")).join("\n");
      return `${headers}\n${rows}`;
    },
    openPopup(dateOfReport, organization_id, pmsName) {
      if (dateOfReport) {
        this.currentReportDate = dateOfReport;
        this.selectedOrganizationId = organization_id;
        this.isPopupVisible = true;
        this.pmsName = pmsName;
      }
    },
    async sendEmail() {
      try {
        const response = await this.$apollo.query({
          query: SEND_TENANT_LEDGER_EMAIL,
          variables: {
            organizationId: this.currentOrganizationId,
            nowUtc: this.currentReportDate,
            email: this.emailId,
            pmsName: this.pmsName,
          },
        });
        if (response.data && response.data.sendEmail.status === "success") {
          this.addAlert({
            type: "success",
            message:
              response.data.sendEmail.message || "Email sent successfully",
            timeout: 5,
          });
        } else {
          this.addAlert({
            type: "error",
            message: "Unable to send email. Please try again.",
            timeout: 5,
          });
        }
      } catch (error) {
        console.log(error);
        this.addAlert({
          type: "error",
          message: "Unable to send email. Please try again.",
          timeout: 5,
        });
      } finally {
        this.isPopupVisible = false;
      }
    },
    cancel() {
      this.isPopupVisible = false;
    },
  },
};
</script>
<style scoped>
.btn-container {
  display: flex;
  gap: 1rem;
}
</style>
